import { useEffect, useState, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Printer, Moon } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";

const fmt = (n) => new Intl.NumberFormat("ar-SA", { maximumFractionDigits: 2 }).format(n || 0);

const COMMERCIAL_COLS = {
  name: { label: "الاسم", get: (b) => b.name },
  national_id: { label: "الهوية", get: (b) => b.national_id, className: "tabular-nums" },
  phone: { label: "الجوال", get: (b) => b.phone, className: "tabular-nums" },
  gender: { label: "الجنس", get: (b) => (b.gender === "male" ? "ذكر" : "أنثى") },
  nationality: { label: "الجنسية", get: (b) => b.nationality },
  campaign: { label: "الحملة", get: (b) => "" }, // enriched via lookup below
  kind: { label: "النوع", get: (b) => (b.registration_kind === "family" ? "عائلة" : "فردي") },
  payment_method: { label: "الدفع", get: (b) => (b.payment_method === "transfer" ? "تحويل" : b.payment_method === "cash" ? "نقدي" : "—") },
  amount: { label: "المبلغ", get: (b) => fmt(b.amount), className: "font-bold" },
  status: { label: "الحالة", get: (b) => (b.status === "confirmed" ? "مؤكد" : b.status === "pending" ? "قيد المراجعة" : b.status === "failed" ? "مرفوض" : "—") },
  room: { label: "الغرفة", get: (b) => b.room_number || "—" },
};

const CHARITY_COLS = {
  name: { label: "الاسم", get: (b) => b.name },
  national_id: { label: "الهوية", get: (b) => b.national_id, className: "tabular-nums" },
  phone: { label: "الجوال", get: (b) => b.phone, className: "tabular-nums" },
  gender: { label: "الجنس", get: (b) => (b.gender === "male" ? "ذكر" : "أنثى") },
  nationality: { label: "الجنسية", get: (b) => b.nationality },
  campaign: { label: "الحملة", get: (b) => "" },
  occurrence: { label: "التسجيل رقم", get: (b) => `#${b.occurrence}` },
};

export default function PrintPage() {
  const { portal, kind } = useParams();
  const [params] = useSearchParams();
  const campaignId = params.get("campaign");
  const colsParam = params.get("cols");
  const queryFilter = params.get("q");
  const statusFilter = params.get("status");
  const [settings, setSettings] = useState({});
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      const s = await api.get("/settings").then((r) => r.data).catch(() => ({}));
      setSettings(s);
      if (kind === "dashboard" && portal === "commercial") {
        const stats = await api.get("/stats/commercial").then((r) => r.data);
        setData({ type: "stats", stats, title: "لوحة تحكم البوابة التجارية" });
      } else if (kind === "beneficiaries") {
        const params = { portal };
        if (campaignId) params.campaign_id = campaignId;
        if (queryFilter) params.q = queryFilter;
        if (statusFilter) params.status = statusFilter;
        const [b, c] = await Promise.all([
          api.get("/beneficiaries", { params }),
          api.get("/campaigns", { params: { portal } }),
        ]);
        setData({ type: "beneficiaries", items: b.data, campaigns: c.data, title: portal === "commercial" ? "قائمة المستفيدين — البوابة التجارية" : "قائمة المستفيدين — البوابة الخيرية" });
      } else if (kind === "expenses") {
        const [e, c] = await Promise.all([
          api.get("/expenses"),
          api.get("/campaigns", { params: { portal: "commercial" } }),
        ]);
        setData({ type: "expenses", items: e.data, campaigns: c.data, title: "تقرير المصروفات" });
      } else if (kind === "income") {
        // Prefer the snapshot stored by the Income page so printed values EXACTLY match what user saw
        const cached = sessionStorage.getItem("umrah_print_income");
        if (cached) {
          try {
            const snap = JSON.parse(cached);
            const cs = await api.get("/campaigns", { params: { portal: "commercial" } });
            const campaign = cs.data.find((x) => x.id === snap.campaignId);
            setData({
              type: "income",
              stats: snap.stats,
              beneficiaries: snap.beneficiaries || [],
              expenses: snap.expenses || [],
              revenues: snap.revenues || [],
              campaign,
              title: `قائمة الدخل${campaign ? ` — ${campaign.name}` : ""}`,
            });
            return;
          } catch (_) { /* ignore, will fall back to fresh fetch */ }
        }
        const p = campaignId ? { campaign_id: campaignId } : {};
        const [stats, b, ex, rv, c] = await Promise.all([
          api.get("/stats/commercial", { params: p }),
          api.get("/beneficiaries", { params: { portal: "commercial", ...p } }),
          api.get("/expenses", { params: p }),
          api.get("/revenues", { params: p }),
          api.get("/campaigns", { params: { portal: "commercial" } }),
        ]);
        const campaign = c.data.find((x) => x.id === campaignId);
        setData({ type: "income", stats: stats.data, beneficiaries: b.data, expenses: ex.data, revenues: rv.data, campaign, title: `قائمة الدخل${campaign ? ` — ${campaign.name}` : ""}` });
      } else if (kind === "dashboard" && portal === "charitable") {
        const stats = await api.get("/stats/charitable").then((r) => r.data);
        setData({ type: "charity-stats", stats, title: "لوحة تحكم البوابة الخيرية" });
      } else if (kind === "report" && portal === "charitable") {
        const [stats, b] = await Promise.all([
          api.get("/stats/charitable"),
          api.get("/beneficiaries", { params: { portal: "charitable" } }),
        ]);
        setData({ type: "charity-report", stats: stats.data, items: b.data, title: "تقرير البوابة الخيرية" });
      }
    })();
  }, [portal, kind, campaignId]);

  if (!data) return <div className="p-10 text-center text-slate-500">جارٍ التحميل...</div>;

  return (
    <div className="min-h-screen bg-white p-8 max-w-5xl mx-auto text-emerald-950">
      <div className="flex justify-end mb-6 no-print">
        <Button onClick={() => window.print()} className="bg-emerald-900 hover:bg-emerald-800 text-white">
          <Printer className="h-4 w-4 ml-2" /> طباعة الآن
        </Button>
      </div>

      <header className="flex items-center justify-between border-b-2 border-emerald-900 pb-4 mb-8">
        <div className="flex items-center gap-4">
          {settings.logo_base64 ? (
            <img src={settings.logo_base64} alt="" className="h-16 w-16 rounded-xl object-cover" />
          ) : (
            <div className="h-16 w-16 rounded-xl bg-emerald-900 flex items-center justify-center">
              <Moon className="h-8 w-8 text-amber-400" />
            </div>
          )}
          <div>
            <h1 className="font-heading text-2xl font-black">{settings.association_name || "جمعية العمرة"}</h1>
            <p className="text-slate-600 text-sm">{data.title}</p>
          </div>
        </div>
        <div className="text-left text-sm text-slate-600">
          <div>تاريخ الطباعة</div>
          <div className="font-bold text-emerald-950">{new Date().toLocaleDateString("ar-SA")}</div>
        </div>
      </header>

      {data.type === "stats" && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <StatBox label="إجمالي الإيرادات" value={`${fmt(data.stats.revenue)} ر.س`} />
          <StatBox label="إجمالي المصروفات" value={`${fmt(data.stats.expenses)} ر.س`} />
          <StatBox label="صافي الدخل" value={`${fmt(data.stats.net_income)} ر.س`} />
          <StatBox label="عدد المستفيدين" value={fmt(data.stats.beneficiaries_count)} />
          <StatBox label="عدد الحملات" value={fmt(data.stats.campaigns_count)} />
        </div>
      )}

      {data.type === "charity-stats" && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatBox label="إجمالي التسجيلات" value={fmt(data.stats.total_registrations)} />
          <StatBox label="مستفيدون فريدون" value={fmt(data.stats.unique_beneficiaries)} />
          <StatBox label="التكرارات" value={fmt(data.stats.duplicates)} />
          <StatBox label="الحملات" value={fmt(data.stats.campaigns_count)} />
        </div>
      )}

      {data.type === "beneficiaries" && (() => {
        const isCommercial = portal === "commercial";
        const colsMap = isCommercial ? COMMERCIAL_COLS : CHARITY_COLS;
        const allKeys = Object.keys(colsMap);
        const selected = colsParam ? colsParam.split(",").filter((k) => colsMap[k]) : allKeys;
        // Enrich campaign lookup
        const campaignById = new Map(data.campaigns.map((c) => [c.id, `#${c.number} ${c.name}`]));
        const cellValue = (colKey, b) => {
          if (colKey === "campaign") return campaignById.get(b.campaign_id) || "—";
          const col = colsMap[colKey];
          return col ? col.get(b) : "—";
        };
        const totalAmount = isCommercial
          ? data.items.reduce((s, x) => s + (Number(x.amount) || 0), 0)
          : 0;
        return (
          <>
            <div className="mb-3 flex justify-between items-center text-sm">
              <div className="font-bold text-emerald-950">إجمالي المستفيدين: {data.items.length}</div>
              {isCommercial && totalAmount > 0 && selected.includes("amount") && (
                <div className="font-bold text-emerald-950">مجموع المبالغ: {fmt(totalAmount)} ر.س</div>
              )}
            </div>
            <table className="w-full text-right border-collapse text-sm">
              <thead className="bg-emerald-900 text-white">
                <tr>
                  <th className="p-2 border border-emerald-900">#</th>
                  {selected.map((k) => (
                    <th key={k} className="p-2 border border-emerald-900">{colsMap[k].label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.items.flatMap((b, i) => {
                  const rows = [
                    <tr key={b.id} className={i % 2 ? "bg-slate-50" : ""}>
                      <td className="p-2 border">{i + 1}</td>
                      {selected.map((k) => (
                        <td key={k} className={`p-2 border ${colsMap[k].className || ""}`}>
                          {cellValue(k, b)}
                        </td>
                      ))}
                    </tr>,
                  ];
                  if (isCommercial && b.companions && b.companions.length > 0) {
                    b.companions.forEach((c, ci) => {
                      rows.push(
                        <tr key={`${b.id}-c-${ci}`} className={i % 2 ? "bg-slate-100" : "bg-slate-50"}>
                          <td className="p-2 border text-xs">↳</td>
                          {selected.map((k) => {
                            let val = "—";
                            if (k === "name") {
                              return (
                                <td key={k} className="p-2 border">
                                  <span className="inline-flex items-center gap-2">
                                    <span>{c.name}</span>
                                    <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-amber-200 text-amber-900">مرافق</span>
                                  </span>
                                </td>
                              );
                            }
                            if (k === "national_id") val = c.national_id;
                            else if (k === "phone") val = c.phone || "—";
                            else if (k === "gender") val = c.gender === "male" ? "ذكر" : "أنثى";
                            else if (k === "nationality") val = c.nationality || "—";
                            else if (k === "room") val = c.room_number || "—";
                            return <td key={k} className={`p-2 border ${colsMap[k].className || ""}`}>{val}</td>;
                          })}
                        </tr>
                      );
                    });
                  }
                  return rows;
                })}
                {isCommercial && totalAmount > 0 && selected.includes("amount") && (
                  <tr className="bg-emerald-900 text-white font-bold">
                    <td className="p-2 border" colSpan={selected.indexOf("amount") + 1}>الإجمالي</td>
                    <td className="p-2 border tabular-nums">{fmt(totalAmount)}</td>
                    {selected.length - selected.indexOf("amount") - 1 > 0 && (
                      <td className="p-2 border" colSpan={selected.length - selected.indexOf("amount") - 1}></td>
                    )}
                  </tr>
                )}
              </tbody>
            </table>
          </>
        );
      })()}

      {data.type === "expenses" && (
        <table className="w-full text-right border-collapse">
          <thead className="bg-emerald-900 text-white">
            <tr>
              <th className="p-2 border border-emerald-900">التاريخ</th>
              <th className="p-2 border border-emerald-900">الفئة</th>
              <th className="p-2 border border-emerald-900">المبلغ</th>
              <th className="p-2 border border-emerald-900">ملاحظات</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((x, i) => (
              <tr key={x.id} className={i % 2 ? "bg-slate-50" : ""}>
                <td className="p-2 border tabular-nums">{x.date}</td>
                <td className="p-2 border">{x.category}</td>
                <td className="p-2 border font-bold">{fmt(x.amount)}</td>
                <td className="p-2 border">{x.notes || "—"}</td>
              </tr>
            ))}
            <tr className="bg-emerald-900 text-white font-bold">
              <td className="p-2 border" colSpan={2}>الإجمالي</td>
              <td className="p-2 border" colSpan={2}>{fmt(data.items.reduce((s, x) => s + x.amount, 0))} ر.س</td>
            </tr>
          </tbody>
        </table>
      )}

      {data.type === "income" && (
        <div className="space-y-6">
          <table className="w-full text-right border-collapse" data-testid="print-income-table">
            <tbody>
              <tr><td className="p-3 border font-bold">إيرادات الرابط الذاتي (تسجيل عام)</td><td className="p-3 border tabular-nums text-left">{fmt(data.stats.public_beneficiary_revenue)} ر.س</td></tr>
              <tr><td className="p-3 border font-bold">إيرادات التسجيل اليدوي (الحضوري)</td><td className="p-3 border tabular-nums text-left">{fmt(data.stats.manual_beneficiary_revenue)} ر.س</td></tr>
              <tr><td className="p-3 border font-bold">إيرادات يدوية أخرى (تبرعات/رعاية)</td><td className="p-3 border tabular-nums text-left">{fmt(data.stats.manual_revenue)} ر.س</td></tr>
              <tr className="bg-emerald-50"><td className="p-3 border font-bold">إجمالي الإيرادات</td><td className="p-3 border tabular-nums text-left font-bold">{fmt(data.stats.revenue)} ر.س</td></tr>
              <tr><td className="p-3 border font-bold">عدد المستفيدين</td><td className="p-3 border tabular-nums text-left">{fmt(data.stats.beneficiaries_count)}</td></tr>
              <tr><td className="p-3 border font-bold">إجمالي المصروفات</td><td className="p-3 border tabular-nums text-left text-amber-700 font-bold">({fmt(data.stats.expenses)}) ر.س</td></tr>
              <tr className="bg-emerald-900 text-white"><td className="p-3 border border-emerald-900 font-black text-lg">صافي الدخل</td><td className="p-3 border border-emerald-900 tabular-nums text-left font-black text-lg">{fmt(data.stats.net_income)} ر.س</td></tr>
            </tbody>
          </table>
        </div>
      )}

      {data.type === "charity-report" && (
        <>
          <div className="grid grid-cols-3 gap-4 mb-8">
            <StatBox label="إجمالي التسجيلات" value={fmt(data.stats.total_registrations)} />
            <StatBox label="فريدون" value={fmt(data.stats.unique_beneficiaries)} />
            <StatBox label="تكرارات" value={fmt(data.stats.duplicates)} />
          </div>
          <table className="w-full text-right border-collapse">
            <thead className="bg-emerald-900 text-white">
              <tr>
                <th className="p-2 border border-emerald-900">#</th>
                <th className="p-2 border border-emerald-900">الاسم</th>
                <th className="p-2 border border-emerald-900">الهوية</th>
                <th className="p-2 border border-emerald-900">التسجيل رقم</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((b, i) => (
                <tr key={b.id} className={i % 2 ? "bg-slate-50" : ""}>
                  <td className="p-2 border">{i + 1}</td>
                  <td className="p-2 border">{b.name}</td>
                  <td className="p-2 border tabular-nums">{b.national_id}</td>
                  <td className="p-2 border">#{b.occurrence}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      <footer className="mt-8 pt-4 border-t border-slate-200 text-center text-xs text-slate-500">
        {settings.association_name} — طُبع عبر نظام إدارة الجمعية · {new Date().toLocaleString("ar-SA")}
      </footer>
    </div>
  );
}

function StatBox({ label, value }) {
  return (
    <div className="border-2 border-emerald-900/20 rounded-lg p-4">
      <div className="text-xs text-slate-600 font-bold mb-1">{label}</div>
      <div className="font-heading text-xl font-black text-emerald-950 tabular-nums">{value}</div>
    </div>
  );
}
